// TanStack Query hooks for the owner event-ticket surfaces
// (SLICE-EVENT-TICKET-PASS). Summary (per-tier counts), issue, and void.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ownerApi } from '../../ownerApi.js'
import type { EventSeat } from '@naya/shared'

export interface EventTierSummary {
  id: string
  name: string
  price_centavos: number | null
  capacity: number | null
  issued: number
  remaining: number | null
}

export interface EventSummary {
  program_id: string
  status: 'draft' | 'active' | 'archived'
  public_code: string | null
  capacity: number | null
  issued: number
  remaining: number | null
  tiers: EventTierSummary[]
}

export interface IssuedTicket {
  card_id: string
  card_code: string
  save_url: string | null
}

export interface IssueTicketsResponse {
  tier_name: string
  holder_name: string | null
  tickets: IssuedTicket[]
  providers_available: string[]
}

export const eventSummaryKey = (id: string) =>
  ['owner', 'event-summary', id] as const

export function useEventSummary(programId: string) {
  return useQuery<{ summary: EventSummary }>({
    queryKey: eventSummaryKey(programId),
    refetchOnMount: 'always',
    queryFn: () =>
      ownerApi.get<{ summary: EventSummary }>(
        `/owner/events/${programId}/summary`,
      ),
  })
}

export function useIssueTickets(programId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      tier_id?: string
      quantity: number
      holder_name?: string
      seat?: EventSeat
    }) =>
      ownerApi.post<IssueTicketsResponse>(
        `/owner/events/${programId}/tickets`,
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: eventSummaryKey(programId) })
    },
  })
}

export function useVoidTicket(programId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (cardId: string) =>
      ownerApi.post(`/owner/tickets/${cardId}/void`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: eventSummaryKey(programId) })
    },
  })
}
